import {
	getNotePage,
	PageParams,
	replyToNote,
} from '@front-app/services/NoteApi/NoteApi';
import {
	ref,
	Ref,
	nextTick,
	computed,
} from '@vue/composition-api';
import { isNoteMessageEqual, NoteMessage, NoteSortDirection } from '@front-app/services/NoteApi/NoteModels';
import { PaginationConfig, usePagination } from '@front-app/hooks/usePagination';

const PAGE_SIZE = 10;

const PAGE_FORWARD = {
	beforeLimit: 0,
	afterLimit: PAGE_SIZE,
	sortDirection: NoteSortDirection.ASC,
};

const PAGE_BACKWARD = {
	beforeLimit: PAGE_SIZE,
	afterLimit: 0,
	sortDirection: NoteSortDirection.ASC,
};

export interface NotePageStore {
	messages: Ref<NoteMessage[]>;
	canPageBackward: Ref<boolean>;
	canPageForward: Ref<boolean>;
	isLoadingBackward: Ref<boolean>;
	isLoadingForward: Ref<boolean>;
	pageForward: () => Promise<void>;
	pageBackward: () => Promise<void>;
	reply(message: string): Promise<NoteMessage>;
}

export const getNotePageStore = (
	noteId: string,
	initialMessages: NoteMessage[],
	config: PaginationConfig,
): NotePageStore => {
	const messages = ref([...initialMessages]);
	const pagination = usePagination(messages, config, isNoteMessageEqual);
	const isAtEndForward = ref(!pagination.canPageForward.value);
	const isAtEndBackward = ref(!pagination.canPageBackward.value);
	const allIds = computed(() => messages.value.reduce((acc, curr) => {
		acc[curr.noteMessageId] = true;
		return acc;
	}, {} as Record<string, boolean>));

	const isLoadingForward = ref(false);
	const pageForward = async (): Promise<void> => {
		pagination.pageForward(PAGE_SIZE);
		if (isAtEndForward.value || isLoadingForward.value) {
			return;
		}
		const params = getForwardParams(messages.value);
		const newMessages = await safeGetMessages(noteId, params, isLoadingForward);
		const filteredMessages = newMessages.filter((_) => !allIds.value[_.noteMessageId]);
		// eslint-disable-next-line require-atomic-updates,no-param-reassign
		messages.value = messages.value.concat(filteredMessages);
		if (filteredMessages.length === 0) {
			// eslint-disable-next-line require-atomic-updates
			isAtEndForward.value = true;
		}
	};

	const isLoadingBackward = ref(false);
	const pageBackward = async (): Promise<void> => {
		pagination.pageBackward(PAGE_SIZE);
		if (isAtEndBackward.value || isLoadingBackward.value) {
			return;
		}
		isLoadingBackward.value = true;
		const params = getBackwardParams(messages.value);
		const newMessages = await safeGetMessages(noteId, params, isLoadingBackward);
		const filteredMessages = newMessages
			.filter((_) => !messages.value.find((m) => m.noteMessageId === _.noteMessageId));
		// eslint-disable-next-line require-atomic-updates,no-param-reassign
		messages.value = filteredMessages.concat(messages.value);
		if (filteredMessages.length === 0) {
			// eslint-disable-next-line require-atomic-updates
			isAtEndBackward.value = true;
		}
	};

	const reply = async (message: string): Promise<NoteMessage> => {
		const newMessage = await replyToNote(noteId, { message });
		// eslint-disable-next-line no-param-reassign
		messages.value = [
			...messages.value.slice(0, pagination.endIdx.value + 1),
			newMessage,
			...messages.value.slice(pagination.endIdx.value + 1, messages.value.length),
		];
		await nextTick();
		pagination.pageForward(1);
		return newMessage;
	};

	return {
		messages: pagination.visible,
		canPageBackward: pagination.canPageBackward,
		canPageForward: pagination.canPageForward,
		isLoadingBackward,
		isLoadingForward,
		pageForward,
		pageBackward,
		reply,
	};
};

function getForwardParams(messages: NoteMessage[]) {
	return {
		fromMessageId: messages[messages.length - 1].noteMessageId,
		...PAGE_FORWARD,
	};
}

function getBackwardParams(messages: NoteMessage[]) {
	return {
		fromMessageId: messages[0].noteMessageId,
		...PAGE_BACKWARD,
	};
}

async function safeGetMessages(noteId: string, params: PageParams, isLoading: Ref<boolean>) {
	// eslint-disable-next-line no-param-reassign
	isLoading.value = true;
	try {
		const { messages } = await getNotePage(noteId, params);
		// eslint-disable-next-line no-param-reassign
		isLoading.value = false;
		return messages;
	} catch (e) {
		// eslint-disable-next-line no-param-reassign
		isLoading.value = false;
		throw e;
	}
}
